import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Users2, Search, UserPlus, BookOpen, GraduationCap, Mail,
    CheckCircle2, Sparkles, Trash2, Send, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
    AlertDialog, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return iso;
    }
}

function KpiTile({ label, value, Icon, testId }) {
    return (
        <Card className="surface-card border-border" data-testid={testId}>
            <CardContent className="p-5">
                <div className="flex items-center justify-between">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                        {label}
                    </div>
                    {Icon && (
                        <div className="h-8 w-8 rounded-md bg-primary/8 text-primary flex items-center justify-center">
                            <Icon className="h-4 w-4" />
                        </div>
                    )}
                </div>
                <div className="font-display text-3xl text-foreground mt-2 leading-none">
                    {value}
                </div>
            </CardContent>
        </Card>
    );
}

export default function AdminInstructors() {
    const [instructors, setInstructors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const [addOpen, setAddOpen] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", employee_id: "" });
    const [creating, setCreating] = useState(false);
    const [resendingId, setResendingId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null); // { id, name, email }
    const [deleting, setDeleting] = useState(false);
    // When the delete endpoint returns 409 (instructor owns programs) we
    // stash the program list here and let the admin pick a new owner from
    // the dropdown. Once reassignment succeeds the delete is retried.
    const [blockedPrograms, setBlockedPrograms] = useState(null); // [{id,title}]
    const [reassignTo, setReassignTo] = useState("");
    const [reassigning, setReassigning] = useState(false);

    const resendSetup = useCallback(async (i) => {
        setResendingId(i.id);
        try {
            await api.post(`/admin/users/${i.id}/send-password-reset`);
            toast.success(`Password setup link sent to ${i.email}.`);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    "Could not send the setup link. Try again.",
            );
        } finally {
            setResendingId(null);
        }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/instructors");
            setInstructors(r.data || []);
        } finally {
            setLoading(false);
        }
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await api.delete(`/admin/users/${deleteTarget.id}`);
            toast.success(
                `${deleteTarget.name || deleteTarget.email} was removed.`,
            );
            setDeleteTarget(null);
            setBlockedPrograms(null);
            setReassignTo("");
            load();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            // 409 with code=instructor_has_programs → switch dialog into
            // reassignment mode rather than dumping a generic toast.
            if (
                e?.response?.status === 409 &&
                detail &&
                typeof detail === "object" &&
                detail.code === "instructor_has_programs"
            ) {
                setBlockedPrograms(detail.programs || []);
                toast.info(
                    `${detail.program_count} program${
                        detail.program_count === 1 ? "" : "s"
                    } need a new owner before this instructor can be deleted.`,
                );
            } else {
                const msg =
                    typeof detail === "string"
                        ? detail
                        : detail?.message ||
                          "Could not delete this instructor. Try again.";
                toast.error(msg);
            }
        } finally {
            setDeleting(false);
        }
    }, [deleteTarget, load]);

    const reassignAndRetry = useCallback(async () => {
        if (!deleteTarget || !reassignTo) return;
        setReassigning(true);
        try {
            const { data } = await api.post(
                `/admin/instructors/${deleteTarget.id}/reassign-programs`,
                { new_instructor_id: reassignTo },
            );
            toast.success(
                `Reassigned ${data.reassigned_count} program${
                    data.reassigned_count === 1 ? "" : "s"
                }. Retrying delete…`,
            );
            // Clear the reassign UI and retry the delete in the same flow.
            setBlockedPrograms(null);
            setReassignTo("");
            await confirmDelete();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    "Could not reassign these programs. Try again.",
            );
        } finally {
            setReassigning(false);
        }
    }, [deleteTarget, reassignTo, confirmDelete]);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return instructors;
        return instructors.filter(
            (i) =>
                i.name?.toLowerCase().includes(q) ||
                i.email?.toLowerCase().includes(q) ||
                i.programs?.some((p) => p.title?.toLowerCase().includes(q)),
        );
    }, [instructors, filter]);

    const totals = useMemo(() => {
        const totalInstructors = instructors.length;
        const totalPrograms = instructors.reduce(
            (acc, i) => acc + (i.program_count || 0),
            0,
        );
        const totalPublished = instructors.reduce(
            (acc, i) => acc + (i.published_count || 0),
            0,
        );
        const totalStudents = instructors.reduce(
            (acc, i) => acc + (i.total_students || 0),
            0,
        );
        return { totalInstructors, totalPrograms, totalPublished, totalStudents };
    }, [instructors]);

    const submitNewInstructor = async () => {
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Name and email are required.");
            return;
        }
        setCreating(true);
        try {
            const { data } = await api.post("/admin/users", {
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                employee_id: form.employee_id.trim() || undefined,
                role: "instructor",
                send_setup_email: true,
            });
            toast.success(
                data?.setup_email_sent
                    ? `Account created — password-setup email sent to ${form.email.trim().toLowerCase()}.`
                    : "Account created, but the setup email didn't go through. Try the 'Send password reset' shortcut on the instructor's profile.",
            );
            setForm({ name: "", email: "", employee_id: "" });
            setAddOpen(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not create instructor");
        } finally {
            setCreating(false);
        }
    };

    return (
        <div
            className="max-w-7xl mx-auto px-6 py-10 pb-24"
            data-testid="admin-instructors-page"
        >
            <Link to="/admin" className="text-sm text-muted-foreground hover:text-primary">
                ← Back
            </Link>
            <div className="flex items-end justify-between mt-2 mb-8 flex-wrap gap-3 anim-fade-up">
                <div>
                    <div className="inline-flex items-center gap-1.5 chip chip-accent">
                        <Users2 className="h-3 w-3" />
                        Faculty
                    </div>
                    <h1 className="font-display text-3xl md:text-4xl text-foreground mt-3 tracking-tight">
                        Instructors
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">
                        Faculty teaching at Heritage. Track program ownership, student
                        engagement, and onboard new instructors.
                    </p>
                </div>
                <Button
                    onClick={() => setAddOpen(true)}
                    className="bg-primary hover:bg-primary/90 gap-1.5 shadow-md shadow-primary/10"
                    size="lg"
                    data-testid="open-add-instructor-btn"
                >
                    <UserPlus className="h-4 w-4" />
                    Add instructor
                </Button>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 anim-stagger">
                <KpiTile
                    label="Total instructors"
                    value={totals.totalInstructors}
                    Icon={Users2}
                    testId="kpi-total-instructors"
                />
                <KpiTile
                    label="Programs authored"
                    value={totals.totalPrograms}
                    Icon={BookOpen}
                    testId="kpi-total-programs"
                />
                <KpiTile
                    label="Published programs"
                    value={totals.totalPublished}
                    Icon={CheckCircle2}
                    testId="kpi-total-published"
                />
                <KpiTile
                    label="Students taught"
                    value={totals.totalStudents}
                    Icon={GraduationCap}
                    testId="kpi-total-students-taught"
                />
            </div>

            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div className="relative w-full sm:w-80">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder="Search name, email or program…"
                        className="pl-9"
                        data-testid="instructors-filter-input"
                    />
                </div>
                <p className="text-xs text-muted-foreground">
                    Showing <strong>{filtered.length}</strong> of {instructors.length}
                </p>
            </div>

            <Card className="surface-card border-border">
                <CardContent className="p-0">
                    <Table className="data-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Instructor</TableHead>
                                <TableHead>Programs</TableHead>
                                <TableHead className="w-32">Students</TableHead>
                                <TableHead className="w-32">Status</TableHead>
                                <TableHead className="w-32">Joined</TableHead>
                                <TableHead className="w-32 text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-12">
                                        <div className="text-sm text-muted-foreground">
                                            Loading instructors…
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : filtered.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-12">
                                        <div className="empty-state !py-8">
                                            <div className="empty-icon-wrap">
                                                <Sparkles className="h-8 w-8 text-primary" />
                                            </div>
                                            <p className="font-display text-lg text-foreground">
                                                {filter ? "No matches" : "No instructors yet"}
                                            </p>
                                            <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                                                {filter
                                                    ? "Try a different search term."
                                                    : "Add your first instructor account to start authoring programs."}
                                            </p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filtered.map((i) => (
                                    <TableRow key={i.id} data-testid={`instructor-row-${i.id}`}>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <div className="h-9 w-9 rounded-full bg-accent/15 text-accent-foreground flex items-center justify-center font-display text-sm ring-1 ring-accent/30">
                                                    {i.name?.charAt(0).toUpperCase() || "?"}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="font-medium text-foreground truncate">
                                                        {i.name}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                                                        <Mail className="h-3 w-3" />
                                                        {i.email}
                                                    </div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {i.programs?.length === 0 ? (
                                                <span className="text-xs text-muted-foreground italic">
                                                    No programs yet
                                                </span>
                                            ) : (
                                                <div className="flex flex-wrap gap-1.5 max-w-md">
                                                    {i.programs.map((p) => (
                                                        <Link
                                                            key={p.id}
                                                            to={`/instructor/programs/${p.id}`}
                                                            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border border-border bg-muted/30 text-foreground hover:bg-primary/8 hover:border-primary/40 transition-colors"
                                                            title={`${p.student_count} students`}
                                                            data-testid={`instructor-program-link-${p.id}`}
                                                        >
                                                            <BookOpen className="h-2.5 w-2.5" />
                                                            {p.title}
                                                            {p.is_published ? (
                                                                <CheckCircle2 className="h-2.5 w-2.5 text-primary ml-0.5" />
                                                            ) : null}
                                                        </Link>
                                                    ))}
                                                </div>
                                            )}
                                            <div className="text-[10px] text-muted-foreground mt-1">
                                                {i.published_count}/{i.program_count} published
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="font-display text-xl text-foreground">
                                                {i.total_students}
                                            </div>
                                            <div className="text-[10px] text-muted-foreground">
                                                across all programs
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {i.is_active ? (
                                                <Badge
                                                    variant="outline"
                                                    className="border-primary/40 text-primary bg-primary/5 text-[10px]"
                                                >
                                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                                    Active
                                                </Badge>
                                            ) : (
                                                <Badge variant="outline" className="text-[10px]">
                                                    Disabled
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground">
                                            {formatDate(i.created_at)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="inline-flex items-center gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    title="Resend password setup link"
                                                    onClick={() => resendSetup(i)}
                                                    disabled={resendingId === i.id}
                                                    data-testid={`instructor-resend-${i.id}`}
                                                    className="h-8 w-8 text-primary hover:bg-primary/10"
                                                >
                                                    {resendingId === i.id ? (
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                    ) : (
                                                        <Send className="h-4 w-4" />
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    title="Delete instructor"
                                                    onClick={() => setDeleteTarget({
                                                        id: i.id,
                                                        name: i.name,
                                                        email: i.email,
                                                    })}
                                                    data-testid={`instructor-delete-${i.id}`}
                                                    className="h-8 w-8 text-rose-600 hover:bg-rose-50"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogContent data-testid="add-instructor-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2">
                            <UserPlus className="h-5 w-5 text-primary" />
                            New instructor
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Full name</Label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Dr. Maria Hernandez"
                                data-testid="add-instructor-name"
                            />
                        </div>
                        <div>
                            <Label>Email</Label>
                            <Input
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                placeholder="instructor@heritage.edu"
                                data-testid="add-instructor-email"
                            />
                        </div>
                        <div>
                            <Label>
                                Employee ID{" "}
                                <span className="font-normal text-muted-foreground">(optional)</span>
                            </Label>
                            <Input
                                value={form.employee_id}
                                onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                                placeholder="e.g. HCC-EMP-042"
                                className="font-mono"
                                data-testid="add-instructor-employee-id"
                            />
                        </div>
                        <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2.5 flex items-start gap-2">
                            <Mail className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-foreground/85">
                                We'll email the instructor a password-setup link.
                                They'll set their own password, then sign in at the
                                Instructor portal. No need to share temporary passwords.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setAddOpen(false)}
                            disabled={creating}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={submitNewInstructor}
                            disabled={creating}
                            className="bg-primary hover:bg-primary/90"
                            data-testid="add-instructor-submit"
                        >
                            {creating ? "Creating…" : "Create & send setup email"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog
                open={!!deleteTarget}
                onOpenChange={(o) => {
                    if (!o && !deleting && !reassigning) {
                        setDeleteTarget(null);
                        setBlockedPrograms(null);
                        setReassignTo("");
                    }
                }}
            >
                <AlertDialogContent
                    className="max-w-lg"
                    data-testid="delete-instructor-dialog"
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display flex items-center gap-2 text-rose-700">
                            <Trash2 className="h-5 w-5" />
                            {blockedPrograms
                                ? "Reassign programs first"
                                : "Delete instructor?"}
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            {blockedPrograms ? (
                                <div className="space-y-3 text-sm">
                                    <p>
                                        <strong>
                                            {deleteTarget?.name || deleteTarget?.email}
                                        </strong>{" "}
                                        still owns{" "}
                                        <strong>{blockedPrograms.length}</strong> active
                                        program{blockedPrograms.length === 1 ? "" : "s"}.
                                        Pick another instructor to take them over, then we'll
                                        retry the delete automatically.
                                    </p>
                                    <ul
                                        className="max-h-32 overflow-y-auto rounded border border-border bg-muted/30 p-2 text-xs space-y-1"
                                        data-testid="reassign-program-list"
                                    >
                                        {blockedPrograms.map((p) => (
                                            <li
                                                key={p.id}
                                                className="flex items-center gap-1.5 text-foreground"
                                            >
                                                <BookOpen className="h-3 w-3 text-muted-foreground" />
                                                {p.title}
                                            </li>
                                        ))}
                                    </ul>
                                    <div>
                                        <Label className="text-xs">
                                            Reassign to
                                        </Label>
                                        <Select
                                            value={reassignTo}
                                            onValueChange={setReassignTo}
                                        >
                                            <SelectTrigger
                                                className="mt-1"
                                                data-testid="reassign-instructor-select"
                                            >
                                                <SelectValue placeholder="Choose another instructor…" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {instructors
                                                    .filter(
                                                        (x) =>
                                                            x.id !== deleteTarget?.id &&
                                                            x.is_active,
                                                    )
                                                    .map((x) => (
                                                        <SelectItem
                                                            key={x.id}
                                                            value={x.id}
                                                            data-testid={`reassign-option-${x.id}`}
                                                        >
                                                            {x.name} · {x.email}
                                                        </SelectItem>
                                                    ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            ) : (
                                <span>
                                    This moves{" "}
                                    <strong>
                                        {deleteTarget?.name || deleteTarget?.email}
                                    </strong>{" "}
                                    to the user trash and blocks their login. They can
                                    be restored later from the All Users page.
                                </span>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting || reassigning}>
                            Cancel
                        </AlertDialogCancel>
                        {blockedPrograms ? (
                            <Button
                                onClick={reassignAndRetry}
                                disabled={!reassignTo || reassigning || deleting}
                                className="bg-primary hover:bg-primary/90 text-white"
                                data-testid="reassign-and-retry-btn"
                            >
                                {reassigning || deleting
                                    ? "Reassigning…"
                                    : "Reassign & delete"}
                            </Button>
                        ) : (
                            <Button
                                onClick={confirmDelete}
                                disabled={deleting}
                                className="bg-rose-600 hover:bg-rose-700 text-white"
                                data-testid="confirm-delete-instructor-btn"
                            >
                                {deleting ? "Deleting…" : "Delete instructor"}
                            </Button>
                        )}
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
